// Keeps the invite code out of Sentry on the signup host (D1 review H1).
//
// The code arrives in the URL fragment. Browsers never send a fragment to a server, but the Sentry SDK
// records URLs itself: its history instrumentation stores `from` / `to` of every pushState,
// replaceState and popstate as a `navigation` breadcrumb (relative URLs, fragment included), and an
// event carries `request.url`. index.js strips the fragment before Sentry.init (sentrySetup.js), so the
// first strip is never recorded; these hooks cover everything after it — a link opened in the same tab
// (hashchange), a URL in a fetch breadcrumb, a code-shaped value in a message. They change telemetry
// only: the link and QR code the page shows after a release are not touched.

/** An invite code (`<public_id>.<mac>`, backend `codes.CODE_RE`) anywhere in a string. */
const CODE_RE = /[a-z2-7]{8,16}\.[A-Za-z0-9_-]{32,}/g;
const FILTERED = '[Filtered]';

/** The URL without its fragment. */
export function dropFragment<T>(value: T): T {
    return (typeof value === 'string' ? value.split('#')[0] : value) as T;
}

function redactCodes<T>(value: T): T {
    return (typeof value === 'string' ? value.replace(CODE_RE, FILTERED) : value) as T;
}

const URL_KEYS = ['from', 'to', 'url'];

/** A breadcrumb with every URL's fragment dropped and code-shaped values redacted. */
export function scrubBreadcrumb(breadcrumb: any): any {
    if (!breadcrumb || typeof breadcrumb !== 'object') {
        return breadcrumb;
    }
    const out = { ...breadcrumb };
    if (out.data && typeof out.data === 'object') {
        const data = { ...out.data };
        URL_KEYS.forEach((key) => {
            if (key in data) {
                data[key] = redactCodes(dropFragment(data[key]));
            }
        });
        out.data = data;
    }
    if ('message' in out) {
        out.message = redactCodes(out.message);
    }
    return out;
}

/** An event with its request URL, Referer, breadcrumbs and stack-frame URLs scrubbed and code-shaped
 *  values redacted from its message and exception values. */
export function scrubEvent(event: any): any {
    if (!event || typeof event !== 'object') {
        return event;
    }
    const out = { ...event };
    if (out.request && typeof out.request === 'object') {
        const request = { ...out.request, url: redactCodes(dropFragment(out.request.url)) };
        if (request.headers && typeof request.headers === 'object' && 'Referer' in request.headers) {
            request.headers = { ...request.headers, Referer: redactCodes(dropFragment(request.headers.Referer)) };
        }
        out.request = request;
    }
    if (Array.isArray(out.breadcrumbs)) {
        out.breadcrumbs = out.breadcrumbs.map(scrubBreadcrumb);
    }
    if ('message' in out) {
        out.message = redactCodes(out.message);
    }
    if (out.exception && Array.isArray(out.exception.values)) {
        out.exception = { ...out.exception, values: out.exception.values.map(scrubException) };
    }
    return out;
}

// An exception's message, and its frames' file URLs (an inline script's frame is the page URL).
function scrubException(value: any): any {
    if (!value || typeof value !== 'object') {
        return value;
    }
    const out = { ...value, value: redactCodes(value.value) };
    if (out.stacktrace && Array.isArray(out.stacktrace.frames)) {
        out.stacktrace = {
            ...out.stacktrace,
            frames: out.stacktrace.frames.map((frame: any) => {
                if (!frame || typeof frame !== 'object') {
                    return frame;
                }
                const clean = { ...frame };
                ['filename', 'abs_path'].forEach((key) => {
                    if (key in clean) {
                        clean[key] = redactCodes(dropFragment(clean[key]));
                    }
                });
                return clean;
            }),
        };
    }
    return out;
}
